// Centralized design tokens — single source of truth for UI polish.
// Static and importable: `import { tokens } from '@styles/tokens'`.
// Use these instead of hardcoding radii, spacing, shadows or brand colors,
// so surfaces share one consistent visual grammar.

export const palette = {
  primary: '#5A58F2',
  primaryStrong: '#4D4BE7',
  primaryFaded: '#EDECFD',
  ink: '#14171F',
  inkStrong: '#0B1C30', // stat-card figures & labels (Figma refonte)
  inkMuted: '#4D5874', // Figma Foreground/neutral-faded
  inkSubtle: '#8893A8',
  border: '#BBC1D3',
  borderFaded: '#DFE2EA',
  borderSubtle: '#E5E7EB',
  surface: '#FFFFFF',
  surfaceMuted: '#F3F4F6',
  surfaceSubtle: '#F8F9FB',
  surfaceFaded: '#FAFAFA', // Figma Background/page-faded — zebra stripe for table even rows
  // Figma Background/neutral-highlighted. Portait le fond de la bande repliée SÉLECTIONNÉE (CODI-800/918) ;
  // CODI-999 a retiré cette card grise, la sélection se lit désormais sur `ink`/`border`. Le token reste dans la
  // palette — c'est une valeur de la maquette, pas un réglage propre à cet écran — mais il n'a plus de lecteur.
  neutralHighlighted: '#D4D8E3',
  // Figma Background/Neutral posé en calque à 40 % : le fond des entrées survolées ou sélectionnées des menus
  // déroulants (CODI-873). Gardé translucide comme la maquette — aplati sur blanc il vaudrait #F2F4F8.
  neutralOverlay: 'rgba(223, 226, 234, 0.4)',
  success: '#05751F',
  successFaded: '#EBFEF6',
  successVivid: '#28B800', // fill-grade green for validate CTAs (success is text-grade)
  danger: '#CB101D',
  dangerFaded: '#FEEFEE',
  critical: '#E22C2C', // Figma Background/Critical — negative stat trends
  criticalFaded: '#FEF1F2', // Figma Background/critical-faded — negative trend pill fill
  criticalBorder: '#FBD5D8', // Figma Border/critical-faded — negative trend pill border
  positive: '#118850', // Figma Background/positive — positive stat trends
  positiveFaded: '#EBFEF6', // Figma Background/positive-faded — positive trend pill fill
  positiveBorder: '#CDEDD5', // Figma Border/positive-faded — positive trend pill border
} as const

// Rationalized radius scale (was a 4/5/6/8/12 mix across components).
export const radius = {
  xxs: '4px', // entrées de menu déroulant (maquette planning CODI-873)
  xs: '6px', // badges, chips, small controls
  sm: '8px', // buttons, inputs, selects, list items
  md: '12px', // cards, dropdowns, popups
  lg: '16px', // large surfaces, modals
  pill: '999px',
} as const

// 4px-based spacing rhythm.
export const space = {
  xxs: '2px',
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  xxxl: '40px',
} as const

// Blue-tinted elevation scale (#101828) — reads cleaner than pure-black shadows.
export const shadow = {
  xs: '0 1px 2px rgba(16, 24, 40, 0.04)',
  sm: '0 1px 2px -1px rgba(16, 24, 40, 0.08), 0 2px 4px -1px rgba(16, 24, 40, 0.06)',
  md: '0 4px 8px -2px rgba(16, 24, 40, 0.08), 0 2px 4px -2px rgba(16, 24, 40, 0.05)',
  lg: '0 12px 24px -6px rgba(16, 24, 40, 0.12), 0 4px 8px -4px rgba(16, 24, 40, 0.06)',
  card: '0 2px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)', // Figma "Raised" — request/composer cards
  overlay: '0 5px 10px rgba(0, 0, 0, 0.05), 0 15px 25px rgba(0, 0, 0, 0.07)', // Figma "Overlay" — floating action cards
  xl: '0 20px 40px -8px rgba(16, 24, 40, 0.16), 0 8px 16px -8px rgba(16, 24, 40, 0.08)',
} as const

export const transition = {
  fast: '120ms cubic-bezier(0.4, 0, 0.2, 1)',
  base: '180ms cubic-bezier(0.4, 0, 0.2, 1)',
} as const

// Accessible focus ring — soft indigo halo, pair with a primary border-color.
export const focusRing = '0 0 0 3px rgba(90, 88, 242, 0.3)'

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const

// Empilement des couches flottantes, de bas en haut. Les panneaux du planning sont DÉPRIORISÉS sous la couche
// modale : une modale ou une popup ouverte DEPUIS un panneau (aperçu de formulaire, localisation, confirmation de
// suppression) doit le recouvrir. Une liste ou un menu ouvert dans une popup passe au-dessus d'elle (`dropdown`) ;
// sur mobile, il s'ouvre dans le panneau du bas (`mobilePanel`). Les notifications recouvrent tout.
export const zIndex = {
  planningPanel: 900,
  modal: 1000,
  dropdown: 1600,
  tooltip: 10000,
  mobilePanel: 10000000,
  toast: 999999999,
  // Niveaux locaux au contexte de Toast.Viewport, indépendants des couches ci-dessus.
  toastClose: 1,
  toastStackBase: 1000,
} as const

export const tokens = {
  palette,
  radius,
  space,
  shadow,
  transition,
  focusRing,
  fontWeight,
  zIndex,
} as const
